import { DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { cn } from 'src/utils/cn';
import BaseModal from './BaseModal';

interface ConfirmModalProps {
  readonly title: string;
  readonly description?: string;
  readonly open: boolean;
  readonly confirmButtonText?: string;
  readonly confirmButtonClassNames?: string;
  readonly cancelButtonText?: string;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly children?: React.ReactNode;
  readonly confirmIcon?: React.ReactNode;
  readonly confirmIconBackground?: string;
}

export const ConfirmModal = ({
  title,
  description,
  open,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  confirmButtonClassNames = 'bg-red-600 hover:bg-red-500 text-white',
  onClose,
  onConfirm,
  children,
  confirmIconBackground = 'bg-red-100',
  confirmIcon = (
    <ExclamationTriangleIcon
      aria-hidden="true"
      className="size-6 text-red-600"
    />
  ),
}: ConfirmModalProps) => {
  return (
    <BaseModal open={open} onClose={onClose} size="lg">
      <div className="sm:flex sm:items-start">
        <div
          className={cn(
            'mx-auto flex size-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10',
            confirmIconBackground,
          )}
        >
          {confirmIcon}
        </div>
        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <DialogTitle
            as="h3"
            className="text-base font-semibold text-gray-900"
          >
            {title}
          </DialogTitle>
          <div className="mt-2">
            {children ? (
              <div className="text-sm text-gray-500">{children}</div>
            ) : (
              <p className="text-sm text-gray-500">{description}</p>
            )}
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          onClick={onConfirm}
          id="confirm-button"
          data-testid="confirm-button"
          className={cn(
            'inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold shadow-xs sm:ml-3 sm:w-auto',
            confirmButtonClassNames,
          )}
        >
          {confirmButtonText}
        </button>
        <button
          type="button"
          data-autofocus
          id="cancel-button"
          data-testid="cancel-button"
          onClick={onClose}
          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0 sm:w-auto"
        >
          {cancelButtonText}
        </button>
      </div>
    </BaseModal>
  );
};
